/**
 * Contract Cache Service
 * Optimizes contract fetching to prevent excessive reads
 *
 * Most students don't have contracts, so we can cache "no contract" results
 * to avoid repeated queries for the same students.
 */

import { collection, query, where, getDocs, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { isDevToolsGateOpen } from '../../../utils/devToolsGate';

class ContractCache {
  constructor() {
    // Cache for active contracts by student ID
    this.contractCache = new Map();

    // Cache for "no contract" results to avoid repeated queries
    this.noContractCache = new Set();

    // Cache TTL
    this.CACHE_TTL = 60000; // 1 minute for contracts
    this.NO_CONTRACT_TTL = 300000; // 5 minutes for "no contract" results

    // Track cache timestamps
    this.cacheTimestamps = new Map();

    // Optional: Listen for all active contracts once
    this.allActiveContracts = new Map();
    this.listener = null;

    // Multi-school support
    this.schoolId = null;

    // Stats
    this.stats = {
      hits: 0,
      misses: 0,
      noContractHits: 0,
      queries: 0
    };
  }

  /**
   * Initialize by loading all active contracts once
   * This is more efficient than querying per student
   * @param {string} schoolId - Required school ID for multi-school support
   */
  async initialize(schoolId) {
    // CRITICAL: Never initialize without schoolId
    if (!schoolId) {
      disciplineLogger.warn('ContractCache.initialize() called without schoolId - skipping');
      return false;
    }

    // If already initialized for this school, skip
    if (this.schoolId === schoolId && this.listener) {
      disciplineLogger.info(`ContractCache already initialized for school: ${schoolId}`);
      return true;
    }

    // Clean up previous listener if switching schools
    if (this.listener) {
      this.listener();
      this.listener = null;
    }

    this.schoolId = schoolId;

    try {
      disciplineLogger.info(`Initializing ContractCache for school: ${schoolId}`);

      const now = Timestamp.now();
      // HIERARCHICAL ONLY: Query from schools/{schoolId}/studentContracts
      const q = query(
        collection(db, 'schools', schoolId, 'studentContracts'),
        where('active', '==', true),
        where('endDate', '>=', now)
      );

      // Set up real-time listener for active contracts
      this.listener = onSnapshot(q, (snapshot) => {
        this.allActiveContracts.clear();

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const studentId = data.studentId;

          // Check if contract is currently active (within date range)
          if (data.startDate <= now && data.endDate >= now) {
            this.allActiveContracts.set(studentId, {
              id: doc.id,
              ...data,
              startDate: data.startDate?.toDate?.() || data.startDate,
              endDate: data.endDate?.toDate?.() || data.endDate
            });
          }
        });

        disciplineLogger.info(`ContractCache loaded ${this.allActiveContracts.size} active contracts for school: ${schoolId}`);

        // Clear individual caches when global cache updates
        this.contractCache.clear();
        this.noContractCache.clear();
        this.cacheTimestamps.clear();
      });

      // Wait for initial load
      await new Promise(resolve => setTimeout(resolve, 100));

      return true;
    } catch (error) {
      disciplineLogger.critical(`Failed to initialize ContractCache for school ${schoolId}:`, error);
      return false;
    }
  }

  /**
   * Get active contract for a student (cached)
   */
  async getActiveContract(studentId) {
    // If we have the global cache, use it
    if (this.allActiveContracts.size > 0 || this.listener) {
      this.stats.hits++;
      const contract = this.allActiveContracts.get(studentId) || null;

      if (!contract) {
        this.stats.noContractHits++;
      }

      disciplineLogger.debug(`ContractCache HIT (global): ${studentId} - ${contract ? 'has contract' : 'no contract'}`);
      return contract;
    }

    // Fallback to individual caching if global cache not available
    return this.getActiveContractWithCache(studentId);
  }

  /**
   * Fallback method with individual caching
   */
  async getActiveContractWithCache(studentId) {
    // CRITICAL: Never query without schoolId
    if (!this.schoolId) {
      disciplineLogger.warn('ContractCache.getActiveContractWithCache() called without schoolId - returning null');
      return null;
    }

    const now = Date.now();

    // Check if we know this student has no contract
    if (this.noContractCache.has(studentId)) {
      const timestamp = this.cacheTimestamps.get(`no-${studentId}`);
      if (timestamp && now - timestamp < this.NO_CONTRACT_TTL) {
        this.stats.noContractHits++;
        disciplineLogger.debug(`ContractCache HIT (no contract): ${studentId}`);
        return null;
      }
      // Expired, remove from cache
      this.noContractCache.delete(studentId);
      this.cacheTimestamps.delete(`no-${studentId}`);
    }

    // Check positive contract cache
    if (this.contractCache.has(studentId)) {
      const timestamp = this.cacheTimestamps.get(studentId);
      if (timestamp && now - timestamp < this.CACHE_TTL) {
        this.stats.hits++;
        const contract = this.contractCache.get(studentId);
        disciplineLogger.debug(`ContractCache HIT: ${studentId}`);
        return contract;
      }
      // Expired, remove from cache
      this.contractCache.delete(studentId);
      this.cacheTimestamps.delete(studentId);
    }

    // Cache miss - query Firebase
    this.stats.misses++;
    this.stats.queries++;

    try {
      const nowTimestamp = Timestamp.now();
      // HIERARCHICAL ONLY: Query from schools/{schoolId}/studentContracts
      const q = query(
        collection(db, 'schools', this.schoolId, 'studentContracts'),
        where('studentId', '==', studentId),
        where('active', '==', true),
        where('startDate', '<=', nowTimestamp),
        where('endDate', '>=', nowTimestamp)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Cache "no contract" result
        this.noContractCache.add(studentId);
        this.cacheTimestamps.set(`no-${studentId}`, now);
        disciplineLogger.debug(`ContractCache MISS (no contract): ${studentId}`);
        return null;
      }

      // Cache the contract
      const doc = snapshot.docs[0];
      const contract = {
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
        endDate: doc.data().endDate?.toDate?.() || doc.data().endDate
      };

      this.contractCache.set(studentId, contract);
      this.cacheTimestamps.set(studentId, now);

      disciplineLogger.debug(`ContractCache MISS (found contract): ${studentId}`);
      return contract;

    } catch (error) {
      disciplineLogger.critical(`Failed to fetch contract for student ${studentId}:`, error);
      return null;
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(1) : 0;

    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
      cachedContracts: this.contractCache.size,
      cachedNoContracts: this.noContractCache.size,
      globalContracts: this.allActiveContracts.size,
      isGlobalCacheActive: !!this.listener
    };
  }

  /**
   * Clear all caches
   */
  clear() {
    this.contractCache.clear();
    this.noContractCache.clear();
    this.cacheTimestamps.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      noContractHits: 0,
      queries: 0
    };
    disciplineLogger.info('ContractCache cleared');
  }

  /**
   * Cleanup listener
   */
  destroy() {
    if (this.listener) {
      this.listener();
      this.listener = null;
    }
    this.clear();
  }
}

// Export singleton instance
export const contractCache = new ContractCache();

// NOTE: ContractCache must be initialized with schoolId by calling:
// contractCache.initialize(schoolId)
// This should be done by components that have access to the school context

// Make available for debugging
if (isDevToolsGateOpen()) {
  window.contractCache = contractCache;
  console.log('📋 Contract Cache available: window.contractCache.getStats()');
  console.log('📋 Initialize with: window.contractCache.initialize(schoolId)');
}
